<template>
  <basic-modal.container
    id="careProviderModal"
    ref="careProviderModalRef"
  >
    <basic-modal.step
      id="search"
      size="full"
      :has-gradient="false"
      :modal-actions="false"
    >
      <basic-modal.content>
        <div class="modal">
          <div
            v-show="showList"
            class="modal__list"
          >
            <faceted-search
              :admit-patients="searchData.admitPatients"
              :avm-network="avmNetwork"
              :contract-start-date="contractStartDate"
              :discount-groups="filterData.discountGroups"
              :location="searchData.location"
              :name="searchData.name"
              @filter="filter"
              @search="search"
            />
            <loading-alt v-if="isLoading" />
            <result-list
              v-else
              v-model:alternative-results="hasOnlyAlternativeResults"
              :auto-scroll="true"
              :avm-network="avmNetwork"
              :contract-start-date="contractStartDate"
              :hovered="hoveredCareProvider"
              :is-avm-network-specific="searchData.name === null"
              :results="filteredSearchResults"
              :selected="selectedCareProvider"
              :show-details="searchData.name !== null"
              @add="add"
              @mouseover="(zsrNumber) => hoverCareProvider(zsrNumber)"
              @mouseout="() => hoverCareProvider()"
              @select="toggleSelectedCareProvider"
            />
          </div>
          <div
            v-show="showMap"
            class="modal__map"
          >
            <google-map
              :avm-network="avmNetwork"
              :has-only-alternative-results="hasOnlyAlternativeResults"
              :hovered="hoveredCareProvider"
              :location="searchData.location"
              :results="filteredSearchResults"
              :selected="selectedCareProvider"
              :zoom-control="!isSMViewport"
              @mouseover="(zsrNumber) => hoverCareProvider(zsrNumber)"
              @mouseout="() => hoverCareProvider()"
              @search="searchByCoordinates"
              @select="toggleSelectedCareProvider"
            />
          </div>
          <div
            v-if="isSMViewport"
            class="modal__switch"
          >
            <basic-switch-button
              v-model="selectedView"
              :items="views"
              name="care-provider-modal-switch"
            />
          </div>
        </div>
      </basic-modal.content>
      <basic-modal.footer />
    </basic-modal.step>
  </basic-modal.container>
</template>

<script setup>
  import { computed, onBeforeMount, onBeforeUnmount, ref, useTemplateRef } from 'vue'

  import { events$ } from '@shared/services'

  import useBasket from '@shared/hooks/useBasket'
  import useBrowser from '@shared/hooks/useBrowser'
  import useI18n from '@shared/hooks/useI18n'
  import useGoogleMap from '@shared/components/CareProvider/useGoogleMap'
  import useLerb from '@shared/hooks/useLerb'
  import usePerson from '@shared/hooks/usePerson'
  import usePersonSpecificProduct from '@shared/hooks/usePersonSpecificProduct'

  import { BasicSwitchButton } from '@shared/components/Basic'
  import BasicModal from '@shared/components/Basic/Modal/index.js'
  import FacetedSearch from '@shared/components/CareProvider/FacetedSearch'
  import GoogleMap from '@shared/components/CareProvider/Map'
  import ResultList from '@shared/components/CareProvider/ResultList.vue'
  import LoadingAlt from '@shared/components/Container/LoadingAlt'

  import { logError } from '@shared/helpers/Logger'

  import { EVENT_CARE_PROVIDER_MODAL } from '@shared/config/events'
  import { CATEGORY_TYPE } from '@shared/config/constants'
  import { AVM_NETWORK, DISCOUNT_GROUPS } from '@shared/components/CareProvider/constants'

  // HOOKS
  const { contractData } = useBasket()
  const { isSMViewport } = useBrowser()
  const { t } = useI18n()
  const { mapCareProviderToDoctor, searchCareProvidersByFormData, searchCareProvidersByMapData } = useLerb()
  const { setMapCenter } = useGoogleMap()
  const { getPerson } = usePerson()
  const { getProductsFromCategory } = usePersonSpecificProduct()

  // DATA
  const VIEW = {
    LIST: 'list',
    MAP: 'map'
  }

  const modalRef = useTemplateRef('careProviderModalRef')

  const avmNetwork = ref(null)
  const cb = ref(() => {})
  const contractStartDate = ref(null)
  const filterData = ref({
    discountGroups: []
  })
  const hasOnlyAlternativeResults = ref(false)
  const hoveredCareProvider = ref(null)
  const isLoading = ref(true)
  const isProductSwitchAllowed = ref(true)
  const searchCoordinates = ref({})
  const searchData = ref({
    admitPatients: false,
    location: null,
    name: null,
  })
  const searchResults = ref([])
  const selectedCareProvider = ref(null)
  const selectedView = ref(VIEW.LIST)
  const supportedAvmNetworks = ref([])

  const views = [
    {
      text: t('careProvider.views.list'),
      value: VIEW.LIST,
    },
    {
      text: t('careProvider.views.map'),
      value: VIEW.MAP,
    }
  ]

  // COMPUTED
  const filteredSearchResults = computed(() => {
    if (filterData.value.discountGroups.length === 0) return []
    // this prevents results with different avmNetwork to be filtered out when searching with name
    if (filterData.value.discountGroups.length === Object.keys(DISCOUNT_GROUPS).length) return searchResults.value
    return searchResults.value.filter(result => filterData.value.discountGroups.includes(result.discounts[avmNetwork.value]))
  })

  const isMapHidden = computed(() => {
    return isSMViewport.value && selectedView.value === VIEW.LIST
  })

  // search in all available avm-networks if search is doctor-specific
  // and product switch is allowed
  const searchAvmNetworks = computed(() => {
    return searchData.value.name && isProductSwitchAllowed.value ? supportedAvmNetworks.value : avmNetwork.value
  })

  const showList = computed(() => {
    return !isSMViewport.value || selectedView.value === VIEW.LIST
  })

  const showMap = computed(() => {
    return !isSMViewport.value || selectedView.value === VIEW.MAP
  })

  // METHODS
  function add(zsrNumber) {
    const doctor = mapCareProviderToDoctor(zsrNumber)

    const avmNetworksOfDoctor = doctor.medicalOfficeNets.map(network => network.avmDefinition)
    const isCurrentAvmNetworkSupported = () => avmNetworksOfDoctor.includes(avmNetwork.value)
    const getSwitchedAvmNetwork = () => supportedAvmNetworks.value
      .filter(network => network !== avmNetwork.value)
      .find(network => supportedAvmNetworks.value.includes(network))

    const actualAvmNetwork = isCurrentAvmNetworkSupported() ? avmNetwork.value : getSwitchedAvmNetwork()
    cb.value({ doctor, productId: AVM_NETWORK[actualAvmNetwork] })
    modalRef.value.close()
  }

  function resetSearchData() {
    searchData.value = {
      admitPatients: false,
      location: null,
      name: null,
    }
  }

  function filter({discountGroups}) {
    filterData.value.discountGroups = discountGroups
  }

  function initData(personId, productId) {
    searchData.value.location = contractData.value.address

    const person = getPerson(personId)
    contractStartDate.value = person.kvgContractStartDate

    avmNetwork.value = Object.keys(AVM_NETWORK).find(key => AVM_NETWORK[key] === productId);
    const kvgProductIds = getProductsFromCategory(CATEGORY_TYPE.KVG, personId).map(product => product.productId)
    supportedAvmNetworks.value = Object.values(AVM_NETWORK)
      .filter(nProductId => kvgProductIds.includes(nProductId))
      .map(nProductId => Object.keys(AVM_NETWORK).find(key => AVM_NETWORK[key] === nProductId))

    filterData.value.discountGroups = Object.values(DISCOUNT_GROUPS).sort((a,b) => a - b)
  }

  function openModal() {
    modalRef.value?.open()
  }

  async function searchByFormData() {
    return await searchCareProvidersByFormData({
      admitPatients: searchData.value.admitPatients,
      avmNetwork: searchAvmNetworks.value,
      contractStartDate: contractStartDate.value,
      doctorName: searchData.value.name,
      zipCode: searchData.value.location?.zipCode,
    })
  }

  async function searchByMapData() {
    return await searchCareProvidersByMapData({
      admitPatients: searchData.value.admitPatients,
      avmNetwork: searchAvmNetworks.value,
      contractStartDate: contractStartDate.value,
      coordinates: searchCoordinates.value,
      doctorName: searchData.value.name,
    })
  }

  async function search({admitPatients, location, name}) {
    try {
      isLoading.value = true

      const hasLocationChanged = location && location.zipCode !== searchData.value.location?.zipCode

      searchData.value = {
        admitPatients,
        location,
        name
      }

      // if only list shown, always use searchByFormData
      if (isMapHidden.value) {
        searchResults.value = await searchByFormData()
        if (hasLocationChanged) {
          setMapCenter(location)
        }
        return
      }

      if (searchData.value.name) {
        searchResults.value = await searchByFormData()

        if (hasLocationChanged) {
          // recenters the map, which triggers a new search if no name is given
          setMapCenter(location)
        }
        return
      }

      if (hasLocationChanged) {
        // recenters the map, which triggers a new search if no name is given
        setMapCenter(location)
        return
      }

      searchResults.value = await searchByMapData()

    } catch (error) {
      logError(error)
    } finally {
      isLoading.value = false
    }
  }

  async function searchByCoordinates(coordinates) {
    searchCoordinates.value = coordinates
    if (searchData.value.name && !isSMViewport.value) {
      return
    }
    try {
      isLoading.value = true
      searchResults.value = await searchByMapData()
    } catch(error) {
      logError(error)
    } finally {
      isLoading.value = false
    }
  }

  function toggleSelectedCareProvider(zsrNumber) {
    const isAlreadySelected = selectedCareProvider.value === zsrNumber
    selectedCareProvider.value = isAlreadySelected ? null : zsrNumber
  }

  function hoverCareProvider(zsrNumber) {
    hoveredCareProvider.value = zsrNumber
  }

  // LIFECYCLE HOOKS
  onBeforeMount(() => {
    // Change product to productId while removing old medicalOfficeSearch (FLEX-5337)
    events$.on(EVENT_CARE_PROVIDER_MODAL.OPEN, async ({ preventProductSwitch, callback, personId, product }) => {
      resetSearchData()
      isProductSwitchAllowed.value = !preventProductSwitch
      initData(personId, product.productId)
      openModal()
      if (callback) cb.value = callback
      if (isMapHidden.value) {
        try {
          isLoading.value = true
          searchResults.value = await searchByFormData()
        } catch (error) {
          logError(error)
        } finally {
          isLoading.value = false
        }
      }
    })

    events$.on(EVENT_CARE_PROVIDER_MODAL.ADD, ({zsrNumber}) => {
      add(zsrNumber)
    })
  })

  onBeforeUnmount(() => {
    events$.off(EVENT_CARE_PROVIDER_MODAL.OPEN, openModal)
  })

</script>

<style>
#careProviderModal .dvp-modal__top {
  position: absolute;
  top: var(--dotcom-responsive-gutter-responsive);
  right: var(--dotcom-responsive-gutter-responsive);
  background-color: var(--surface);
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0;
  border-radius: var(--fixed-border-radius-fix-01);
  height: 50px;
  width: 50px;
  z-index: var(--dvp-stack-level-element);
}

#careProviderModal .dvp-modal__top .close-button {
  display: flex;
  justify-content: center;
  align-items: center;
}
</style>

<style scoped>
.modal {
  --list-width: 400px;
  display: flex;
  flex-direction: row;
  height: 100vh;
}

.modal__list {
  flex: 0 0 100%;
  display: flex;
  flex-direction: column;
  padding: 0 var(--fixed-spacing-fix-06);
}

.modal__map {
  height: 100vh;
  width: 100%;
}

.modal__switch {
  position: absolute;
  bottom: var(--dotcom-responsive-spacing-res-s);
  left: 50%;
  transform: translateX(-50%);
}

@media (--v-large) {
  .modal__list {
    flex: 0 0 var(--list-width);
  }
}
</style>
